import React, { useState, useLayoutEffect, useRef, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Link,
  Theme,
} from '@material-ui/core'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import { FormattedMessage } from 'react-intl'
import ProductCardHeader from './ProductCardHeader'
import { useRouter } from '@src/routes'
import { ListKits } from '@src/types/graphql-types'
import { orderBy, sortBy } from 'lodash'
import DesignAndPriceModal from '../../../components/DesignAndPrice/DesignAndPriceModal'
import DesignAndPriceButton from './DesignAndPriceButton'
import EditAndCloseButtons from './EditAndCloseButtons'
import {
  AssayType,
  AssayTypeOptions,
  MilliplexKitTypes,
  SampleTypeYesNoOptions,
} from '../../../enum'
import { MilliplexFormValues } from '../../../Milliplex'
import { useFormikContext } from 'formik'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { isPremixedProduct } from '../../../utils'

const useStyles = makeStyles((theme: Theme) => {
  const { main, light } = theme.palette.primary
  return {
    root: {
      border: 'solid 1px',
      borderColor: theme.palette.grey[500],
      borderRadius: '5px',
      marginTop: theme.spacing(6),
    },
    header: {
      borderBottom: `solid 1px ${theme.palette.grey[500]}`,
      background: theme.palette.background.grey,
    },
    customPremixedHeaderStyle: {
      backgroundColor: light,
      borderColor: main,
    },
    gridItemBottomMargin: {
      marginBottom: theme.spacing(4),
    },
    gridLastItemBottomMargin: {
      marginBottom: theme.spacing(0),
    },
    gridItemLabel: {
      fontWeight: theme.typography.fontWeightBold,
    },
    buttonContainer: {
      height: 40,
      marginTop: theme.spacing(2),
      display: 'flex',
      justifyContent: 'center',
    },
    kitTypeLabelStyle: {
      marginTop: theme.spacing(2),
    },
    kitTypeValueStyle: {
      marginTop: -4,
      marginLeft: -40,
      height: theme.typography.pxToRem(28),
      display: 'flex',
      alignItems: 'center',
    },
    configurableKitTypeStyle: {
      backgroundColor: theme.palette.primary.light,
      fontWeight: theme.typography.fontWeightBold,
      padding: theme.typography.pxToRem(5),
    },
    productAnalytes: {
      display: 'flex',
    },
    analytesStyle: {
      padding: theme.spacing(0, 4, 0, 0),
    },
    analytesUnderline: {
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        bottom: -4,
        height: '4px',
        width: '90%',
        backgroundColor: theme.palette.warning.main,
        borderRadius: '10px',
      },
    },
    showAllLink: {
      cursor: 'pointer',
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(4),
      display: 'block',
    },
    displayInlineFlex: {
      display: 'inline-flex',
      '& svg': {
        marginLeft: theme.spacing(2),
      },
    },
    analytesItems: {
      display: 'flex',
      flexWrap: 'wrap',
      paddingInline: theme.spacing(1.25),
    },
  }
})

export interface SelectedKit {
  id: string
  panelId: string
  isPremixed: string
  analytes: string[]
  sampleType: string
  spaceSaver: string
  customPremixing: string
  quantity: number
  kitInstrument?: string
  defaultAnalytes?: string[]
}

export interface ProductCardProps {
  milliplexKit: ListKits
  analytes: string[]
}

export const parseParams = (params: {
  [key: string]: string
}): SelectedKit[] => {
  return Object.entries(params).map(([key, value]) => {
    const [
      panelId,
      isPremixed,
      analytes,
      sampleType,
      spaceSaver,
      customPremixing,
      quantity,
      kitInstrument,
      defaultAnalytes,
    ] = value.split('_')
    return {
      id: key,
      panelId,
      isPremixed,
      analytes: analytes?.split(',') || [],
      defaultAnalytes: defaultAnalytes?.split(',') || [],
      sampleType,
      spaceSaver,
      customPremixing,
      quantity: Number(quantity),
      kitInstrument,
    }
  })
}

const ProductCard: React.FC<ProductCardProps> = ({
  milliplexKit,
  analytes: step1SelectedAnalytes,
}) => {
  const classes = useStyles()
  const formik = useFormikContext<MilliplexFormValues>()
  const { query } = useRouter()
  const [isDesignAndPriceModalOpen, setDesignAndPriceModal] =
    useState<boolean>(false)
  const kit = formik.values.selectedKits[milliplexKit.panelId]
  const selectedKit = kit
    ? parseParams({
        [milliplexKit.panelId]: kit.url,
      })[0]
    : null

  const analytesContainerRef = useRef<HTMLDivElement>(null)
  const [showAll, setShowAll] = useState(false)
  const [unmatchedAnalytesRowCount, setUnmatchedAnalytesRowCount] = useState(0)

  const userSelectedAnalytes = selectedKit?.analytes
  const analytes = orderBy(milliplexKit.analytes)

  const sortedAnalytes = sortBy(analytes, (a) => {
    const index = step1SelectedAnalytes.indexOf(a)
    return index >= 0 ? index : 999
  })

  const isPreviouslyViewedKit = query.previouslyViewed === milliplexKit.panelId
  let configurableAnalytes = isPreviouslyViewedKit
    ? userSelectedAnalytes
    : userSelectedAnalytes ||
      milliplexKit?.analytes.filter((analyte) =>
        step1SelectedAnalytes.includes(analyte)
      )
  configurableAnalytes = sortBy(configurableAnalytes, (a) => {
    const index = step1SelectedAnalytes.indexOf(a)
    return index >= 0 ? index : 999
  })

  // TODO Remove customPremixedAvailable from Gql layer if not used anywhere
  const speciesBoxStyle = clsx(
    classes.header,
    selectedKit && classes.customPremixedHeaderStyle
  )

  const isConfigurableKit =
    MilliplexKitTypes[milliplexKit?.assayType] === AssayType.Configurable

  const kitTypeValueStyle = clsx(
    classes.kitTypeValueStyle,
    isConfigurableKit && classes.configurableKitTypeStyle
  )
  let analytesInKit
  const isScreeningAssayType =
    milliplexKit.assayTypeSelection === AssayTypeOptions.Screening

  if (isPreviouslyViewedKit && isScreeningAssayType) {
    analytesInKit = configurableAnalytes.length >= 2 ? configurableAnalytes : []
  } else if (isPreviouslyViewedKit) {
    analytesInKit = configurableAnalytes
  } else {
    analytesInKit = isConfigurableKit ? configurableAnalytes : sortedAnalytes
  }
  const isConfigurableProduct = !isPremixedProduct(milliplexKit)
  const isCustomPremixAvailable = milliplexKit?.customPremixedAvailable?.some(
    (item) =>
      item.toLocaleLowerCase() === SampleTypeYesNoOptions.Yes.toLowerCase()
  )

  const isSpaceSaverAvailable =
    milliplexKit?.spaceSaver?.toLowerCase() ===
    SampleTypeYesNoOptions.Yes.toLowerCase()

  const showAllAnalytes = () => {
    if (analytesContainerRef.current) {
      Array.from(analytesContainerRef.current.childNodes).forEach((node) => {
        if (node instanceof HTMLElement) {
          node.style.display = 'block'
        }
      })
    }
  }

  const renderAnalytes = () => {
    setUnmatchedAnalytesRowCount(0)
    setShowAll(false)
    showAllAnalytes()
    let widthSum = 0
    let unmatchedAnalytesRowCount = 0
    const containerWidth = analytesContainerRef.current?.clientWidth

    analytesContainerRef.current?.childNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        const nodeWidth = node.clientWidth
        widthSum += nodeWidth
        if (containerWidth !== undefined && widthSum >= containerWidth) {
          widthSum = nodeWidth
          if (node.className === 'unmatched-analytes') {
            unmatchedAnalytesRowCount++
            setUnmatchedAnalytesRowCount(unmatchedAnalytesRowCount)
          }
        }
        if (
          unmatchedAnalytesRowCount >= 2 &&
          node.className === 'unmatched-analytes'
        ) {
          node.style.display = 'none'
        }
      }
    })
  }

  const toggleShowAll = () => {
    setShowAll(!showAll)
  }

  useLayoutEffect(() => {
    window.addEventListener('resize', () => {
      showAllAnalytes()
      renderAnalytes()
    })
    return () =>
      window.removeEventListener('resize', () => {
        showAllAnalytes()
        renderAnalytes()
      })
  }, [])

  useEffect(() => {
    if (showAll) {
      let widthSum = 0
      let unmatchedAnalytesRowCount = 0
      const containerWidth = analytesContainerRef.current?.clientWidth

      analytesContainerRef.current?.childNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          const nodeWidth = node.clientWidth
          widthSum += nodeWidth
          if (containerWidth !== undefined && widthSum >= containerWidth - 10) {
            widthSum = nodeWidth
            if (node.className === 'unmatched-analytes') {
              unmatchedAnalytesRowCount++
              setUnmatchedAnalytesRowCount(unmatchedAnalytesRowCount)
            }
          }
        }
      })

      showAllAnalytes()
    } else {
      renderAnalytes()
    }
  }, [showAll, query.analytes, milliplexKit.analytes, analytesInKit])

  return (
    <Card className={classes.root}>
      <CardHeader
        className={speciesBoxStyle}
        title={<ProductCardHeader milliplexKit={milliplexKit} />}
      />
      <CardContent>
        <Grid container>
          <Grid item xs={6}>
            <Grid container className={classes.kitTypeLabelStyle}>
              <Grid item xs={4} className={classes.gridItemBottomMargin}>
                <p className={classes.gridItemLabel}>
                  <FormattedMessage
                    id="MILLIPLEX_KIT_TYPE"
                    defaultMessage="Kit Type"
                  />
                  :
                </p>
              </Grid>
              <Grid item className={kitTypeValueStyle}>
                <p>{MilliplexKitTypes[milliplexKit?.assayType]}</p>
              </Grid>
            </Grid>
            <Grid
              container
              item
              spacing={2}
              className={classes.gridItemBottomMargin}
            >
              <Grid item xs={4}>
                <p className={classes.gridItemLabel}>
                  <FormattedMessage
                    id="MILLIPLEX_ANALYTES_IN_KIT"
                    defaultMessage="Analytes in Kit"
                  />
                  :
                </p>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container xs={6} justifyContent="flex-end">
            <div className={classes.buttonContainer}>
              {selectedKit ? (
                <EditAndCloseButtons
                  onClick={() => {
                    setDesignAndPriceModal(true)
                  }}
                  selectedKit={selectedKit}
                  isCloseButton
                />
              ) : (
                <DesignAndPriceButton
                  onClick={() => {
                    setDesignAndPriceModal(true)
                  }}
                />
              )}
            </div>
          </Grid>

          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              className={classes.gridItemBottomMargin}
            >
              <div ref={analytesContainerRef} className={classes.analytesItems}>
                {analytesInKit.map((analyte, index) => (
                  <div
                    key={index}
                    style={{ marginTop: 10 }}
                    className={`${
                      step1SelectedAnalytes.includes(analyte)
                        ? ''
                        : 'unmatched-analytes'
                    }`}
                  >
                    <p
                      className={`${classes.analytesStyle} ${
                        step1SelectedAnalytes.includes(analyte) &&
                        !isPreviouslyViewedKit
                          ? classes.analytesUnderline
                          : ''
                      }`}
                    >
                      {analyte}
                    </p>
                  </div>
                ))}
              </div>
            </Grid>
            {unmatchedAnalytesRowCount >= 2 && analytesInKit.length > 2 && (
              <Link onClick={toggleShowAll} className={classes.showAllLink}>
                {showAll ? (
                  <span className={classes.displayInlineFlex}>
                    <FormattedMessage
                      id="SHOW_LESS"
                      defaultMessage="Show Less"
                    />
                    <ExpandLessIcon />
                  </span>
                ) : (
                  <span className={classes.displayInlineFlex}>
                    <FormattedMessage id="SHOW_ALL" defaultMessage="Show All" />
                    <ExpandMoreIcon />
                  </span>
                )}
              </Link>
            )}
          </Grid>
          <Grid item xs={6}>
            {selectedKit?.sampleType && (
              <Grid container>
                <Grid item xs={4} className={classes.gridItemBottomMargin}>
                  <p className={classes.gridItemLabel}>
                    {' '}
                    <FormattedMessage
                      id="SAMPLE_TYPE"
                      defaultMessage="Sample Type"
                    />
                    :
                  </p>
                </Grid>

                <Grid item>
                  <p>{selectedKit.sampleType}</p>
                </Grid>
              </Grid>
            )}
            {selectedKit?.customPremixing &&
              isConfigurableProduct &&
              isCustomPremixAvailable && (
                <Grid container>
                  <Grid item xs={4} className={classes.gridItemBottomMargin}>
                    <p className={classes.gridItemLabel}>
                      <FormattedMessage
                        id="CONF_MILLIPLEX_CUSTOM_PREMIX_LABEL_milliplex"
                        defaultMessage="Custom Premixing"
                      />
                      :
                    </p>
                  </Grid>

                  <Grid item>
                    <p>{selectedKit.customPremixing}</p>
                  </Grid>
                </Grid>
              )}
            {selectedKit?.spaceSaver &&
              isSpaceSaverAvailable &&
              isConfigurableProduct && (
                <Grid container>
                  <Grid
                    item
                    xs={4}
                    className={classes.gridLastItemBottomMargin}
                  >
                    <p className={classes.gridItemLabel}>
                      <FormattedMessage
                        id="SPACE_SAVING"
                        defaultMessage="Space Saving"
                      />
                      :
                    </p>
                  </Grid>

                  <Grid item>
                    <p>{selectedKit.spaceSaver}</p>
                  </Grid>
                </Grid>
              )}
          </Grid>
        </Grid>
      </CardContent>
      <DesignAndPriceModal
        open={isDesignAndPriceModalOpen}
        handleClose={() => setDesignAndPriceModal(false)}
        milliplexKit={milliplexKit}
      />
    </Card>
  )
}

export default ProductCard
